<template>
	<div>
		<ui-box v-if="shouldShowSection('basic')">
			<!-- Name -->
			<h5>Name & Description</h5>
			<span class="grey--text text--darken-1 text-body-2">
				Let's get started by giving your pass a strong name and description that gives 
				your prospective audience and idea of what they should expect.
			</span>

			<v-divider class="mb-6 mt-4 opacity-80"></v-divider>

			<v-row>
				<v-col>
					<v-text-field
							v-model="newPass.name"
							class="rounded-lg required"
							hint='Ex: 1 Day Connect Pass'
							label="Pass Name"
							outlined
							persistent-hint
							:rules="rules.name"
							type="text"
					></v-text-field>
				</v-col>
			</v-row>
			<!-- End: Name -->

			<v-row>
				<v-col>
					<v-textarea
							v-model="newPass.mobile_pass_description"
							auto-grow
							class="rounded-lg required"
							counter="250"
							hint=""
							label="Campaign Description"
							outlined
							persistent-hint
							rows="4"
							:rules="rules.mobile_pass_description"
					></v-textarea>
				</v-col>
			</v-row>
			<!-- End: Mobile Pass Description -->
		</ui-box>

		<ui-box v-if="shouldShowSection('campaign_dates')">
			<!-- Campaign Dates -->
			<h5>Important Dates</h5>
			<span class="grey--text text--darken-1 text-body-2">
				These are dates related to the pass build and will be shown to your merchants 
				in their invite.
			</span>

			<v-divider class="mb-6 mt-4 opacity-80"></v-divider>

			<v-row>
				<!-- Kickoff Date -->
				<!-- No longer needed here -->
				<v-col v-if="3 === 1" cols="12" sm="4">
					<bw-date-time-picker
							v-model="newPass.kickoff_date"
							label="Kickoff Date"
							:required="true"
							:rules="rules.kickoff_date"
					></bw-date-time-picker>
				</v-col>
				<!-- End: Kickoff Date -->

				<!-- Launch Date -->
				<v-col cols="12" sm="4">
					<bw-date-time-picker
							v-model="newPass.launch_date"
							label="Launch Date"
							:required="true"
							:rules="rules.launch_date"
					></bw-date-time-picker>
				</v-col>
				<!-- End: Launch Date -->

				<!-- End Date -->
				<v-col cols="12" sm="4">
					<bw-date-time-picker
							v-model="newPass.end_date"
							label="End Date"
							:rules="rules.end_date"
					></bw-date-time-picker>
				</v-col>
			</v-row>
			<!-- End: End Date -->

			<!-- End: Campaign Dates -->
		</ui-box>

		<!-- Product Requirements -->
		<ui-box v-if="shouldShowSection('product_requirements')">

			<v-row class="align-center">
				<v-col>
					<h5>Products</h5>
					<span class="grey--text text--darken-1 text-body-2">
						These are the required or optional products that merchant will need to join your pass.
					</span>
				</v-col>
				<v-col class="ps-4" cols="auto">
					<v-btn
							class="icon-left rounded-lg"
							color="secondary"
							depressed
							@click="showAddProductRequirementForm = true"
					>
						<v-icon>
							mdi-plus
						</v-icon>
						Add
					</v-btn>
				</v-col>
			</v-row>

			<v-divider
					v-if="!newPass.onboard_settings?.productReqs.length"
					class="mb-6 mt-4 opacity-80"
			></v-divider>

			<!-- Add Product Requirement -->
			<!-- TODO: PUT THIS IN A MODAL (even though we may have said not to ;) -->
			<template v-if="showAddProductRequirementForm">
				<v-form
						v-model="formProductRequirementsIsValid"
						class="mb-4 mt-6"
						ref="formProductRequirements"
				>
					<v-row>
						<v-col>
							<v-select
									v-model="productTemplate"
									class="rounded-lg required"
									:items="productTemplates"
									label="Product Template"
									item-text="name"
									item-value="id"
									outlined
									:rules="rules.requirement_product_template"
							></v-select>
						</v-col>
					</v-row>

					<v-row>
						<v-col>
							<v-text-field
									v-model="newRequirement.product_title"
									class="rounded-lg required"
									label="Product Title"
									outlined
									persistent-hint
									type="text"
									:rules="rules.requirement_product_title"
							></v-text-field>
						</v-col>
					</v-row>

					<v-row>
						<v-col>
							<v-text-field
									v-model="newRequirement.product_instructions"
									class="rounded-lg"
									label="Product Instructions"
									outlined
									persistent-hint
									type="text"
							></v-text-field>
						</v-col>
					</v-row>

					<v-row v-if="hasChildTemplates">
						<v-col>
							<v-switch
									v-model="limitChildTemplates"
									class="mt-3"
									dense
									hide-details="auto"
									inset
									label="Limit child templates?"
							></v-switch>
						</v-col>
					</v-row>

					<v-row v-if="hasChildTemplates">
						<v-col>
							<v-select
									v-model="newRequirement.child_templates"
									class="rounded-lg"
									:items="childTemplates"
									label="Child Templates"
									item-text="name"
									item-value="id"
									chips
									multiple
									outlined
							></v-select>
						</v-col>
					</v-row>

					<v-row>
						<v-col>
							<v-btn
									class="me-2 me-md-4 rounded-lg"
									color="grey darken-1"
									depressed
									text
									@click="hideAndCleanProductRequirement"
							>
								Cancel
							</v-btn>
							<v-btn
									class="rounded-lg"
									color="primary"
									depressed
									:disabled="!formProductRequirementsIsValid"
									@click="saveProductRequirement"
							>
								Add
							</v-btn>
						</v-col>
					</v-row>

				</v-form>

			</template>
			<!-- End: Add Product Requirement -->

			<!-- Product Requirement Table -->
			<bw-table
					aria-label="data-table"
					class="mt-6!!!"
					:headers="productReqHeaders"
					hide-default-footer
					:hide-default-header="hideProductReqHeaders"
					id="product-req-table"
					:items="newPass.onboard_settings?.productReqs"
					:showSearch="false"
			>
				<template v-slot:no-data>
					<div class="align-center d-flex flex-column justify-center"
							 style="height: 100%;">
						<template
								v-if="formProductRequirementsMessages.length > 0"
								v-for="msg in formProductRequirementsMessages"
						>
							<div
									:key="msg"
									class="red--text text--darken-1 text-body-2"
							>
								{{ msg }}
							</div>
						</template>
					</div>
				</template>
				<template v-slot:item.product_template_name="{ item: productReq }">
					<div
							class="text-subtitle-2"
							style="flex-wrap: nowrap !important;"
					>
						{{ productReq.product_template_name }}
					</div>
				</template>
				<template v-slot:item.product_title="{ item: productReq }">
					<div class="text-subtitle-2">
						{{ productReq.product_title }}
					</div>
					<div class="grey--text mb-2 text-body-2 text--darken-1">
						{{ productReq.product_instructions }}
					</div>
					<div>

						<v-chip
								v-for="templateId in productReq.child_templates"
								:key="templateId"
								class="me-1"
								color="accent lighten-5"
								label
								light
								small
						>
								<span class="accent--text text--darken-22">
									{{ getChildTemplate(templateId).name }}
								</span>
						</v-chip>

					</div>
				</template>
				<template v-slot:item.actions="{ item: productReq, index }">
					<div class="d-flex">
						<v-btn
								class="me-1"
								color="secondary"
								depressed
								fab
								small
								@click=""
						>
							<v-icon>
								mdi-pencil-outline
							</v-icon>
						</v-btn>
						<v-btn
								color="accent"
								depressed
								fab
								small
								@click=""
						>
							<v-icon>
								mdi-content-copy
							</v-icon>
						</v-btn>
						<v-divider class="mx-2 my-1" vertical></v-divider>
						<v-btn
								color="error"
								depressed
								fab
								small
								@click="removeProductRequirement(index)"
						>
							<v-icon>
								mdi-trash-can-outline
							</v-icon>
						</v-btn>
					</div>
				</template>
			</bw-table>
			<!-- End: Product Requirement Table -->

			<!-- End: Product Requirements -->
		</ui-box>

	</div>

</template>

<script>
import { helpers } from '../../../helpers';

export default {
	name: "PassCommonSection",
	props: {
		newPass: {
			type: Object,
			required: true,
		},
		rules: {
			type: Object,
			required: true,
		},
		showSections: {
			type: Array,
			required: true,
		},
		formProductRequirementsMessages: {
			type: Array,
			required: true,
		},
	},
	data() {
		return {
			formProductRequirementsIsValid: false,
			newRequirement: new ProductRequirement(),
			showAddProductRequirementForm: false,
		};
	},
	computed: {
		productReqHeaders() {
			return [
				{
					align: 'start',
					mandatory: true,
					text: 'Type',
					value: 'product_template_name'
				},
				{
					align: 'start',
					selected: true,
					sortable: false,
					text: 'Product Requirement',
					value: 'product_title'
				},
				{
					align: 'start',
					mandatory: true,
					sortable: false,
					text: 'Actions',
					value: 'actions',
					width: '1%'
				}
			];
		},
		hideProductReqHeaders() {
			return !this.newPass.onboard_settings?.productReqs?.length > 0;
		},
		productTemplates() {
			let templates = this.newPass.productTemplates;
			// if(templates?.length > 0 && this.newPass.onboard_settings?.productReqs?.length > 0) {
			// 	templates = this.newPass.productTemplates.filter((pt) => {
			// 		const productReqTypes = this.newPass.onboard_settings.productReqs.map((pr) => pr.product_template_type);
			// 		return !productReqTypes.includes(pt.type);
			// 	});
			// }
			if(templates?.length > 0) {
				return [{ id: null, name: '- Select One -' }, ...templates];
			}
			else {
				return [];
			}
		},
		productTemplate: {
			get() {
				return this.newRequirement.product_template;
			},
			set(value) {
				if(value) {
					this.$set(this.newRequirement, 'product_template', value);
					const productTemplate = this.newPass.productTemplates.find((pt) => pt.id === value);
					this.$set(this.newRequirement, 'product_template_name', productTemplate.name);
					this.$set(this.newRequirement, 'product_template_type', productTemplate.type);
				}
				else {
					this.$set(this.newRequirement, 'product_template', null);
					this.$set(this.newRequirement, 'product_template_name', null);
					this.$set(this.newRequirement, 'product_template_type', null);
				}
			}
		},
		hasChildTemplates() {
			if(this.productTemplate) {
				const template = this.newPass.productTemplates.find((pt) => pt.id === this.productTemplate && pt.children_allowed);
				return template?.children?.length > 0;
			}
			return false;
		},
		limitChildTemplates: {
			get() {
				if(this.hasChildTemplates && this.newRequirement.limit_child_templates == null) {
					this.newRequirement.limit_child_templates = false;
				}
				return this.newRequirement.limit_child_templates;
			},
			set(value) {
				this.newRequirement.limit_child_templates = value;
			}
		},
		childTemplates() {
			if(this.productTemplate) {
				const template = this.newPass.productTemplates.find((pt) => pt.id === this.productTemplate && pt.children_allowed);
				return template.children;
			}
			else {
				return null;
			}
		},
		childTemplatesDisplay() {
			return this.newPass.productTemplates
					.filter((pt) => pt.children)
					.map((pt) => pt.children)
					.flat();
		},
	},
	methods: {
		getChildTemplate(templateId) {
			return this.childTemplatesDisplay.filter(template => template.id === templateId)[0];
		},
		getDisplayLocalDate(date) {
			return helpers.getDisplayLocalDate(date);
		},
		getDisplayLocalDateTime(dateTime) {
			return helpers.getDisplayLocalDateTime(dateTime);
		},
		hideAndCleanProductRequirement() {
			this.showAddProductRequirementForm = false;
			this.newRequirement = new ProductRequirement();
		},
		removeProductRequirement(index) {
			this.newPass.onboard_settings.productReqs.splice(index, 1);
		},
		saveProductRequirement() {
			if(!this.newPass.onboard_settings) this.$set(this.newPass, 'onboard_settings', {});
			if(!this.newPass.onboard_settings.productReqs) this.$set(this.newPass.onboard_settings, 'productReqs', []);
			this.newPass.onboard_settings.productReqs.push(this.newRequirement);

			this.hideAndCleanProductRequirement();
		},
		shouldShowSection(section) {
			return !this.showSections.length || this.showSections.includes(section);
		}
	}
}

function ProductRequirement() {
	this.product_template = null;
	this.product_template_name = null;
	this.product_template_type = null;
	this.product_title = null;
	this.product_instructions = null;
	this.limit_child_templates = null;
}

</script>

<style lang="scss" scoped>
::v-deep #product-req-table td {
	vertical-align: top !important;
}
</style>
